import { ErpButtonRaw, ErpDiv, ErpSpan } from "@/components/erp";
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@/components/providers/ThemeProvider'
import { Gem, Monitor, Package, Palette, Smile, Plus, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import ThemeBuilderModal from './ThemeBuilderModal';

// Custom themes round-trip through localStorage ('vite-ui-custom-themes') and
// are therefore hand-editable, so the swatch colour is untrusted input. Only a
// plain hex colour (what ThemeBuilderModal's colour inputs produce) reaches the
// inline style; anything else falls back to the neutral border. This mirrors
// the check generateThemeVariables()/hexToHSL already applies when the theme
// CSS variables are generated in ThemeProvider.
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

function swatchStyle(primaryColor: unknown): React.CSSProperties | undefined {
  return typeof primaryColor === 'string' && HEX_COLOR.test(primaryColor)
    ? { backgroundColor: primaryColor }
    : undefined;
}

export const ThemeSwitcher: React.FC = () => {
  const { theme, setTheme, customThemes, removeCustomTheme } = useTheme()
  const { t } = useTranslation()
  const [showBuilder, setShowBuilder] = useState(false)

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <ErpButtonRaw 
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
            title={t('Theme')}
          >
            <Palette size={20} className="text-lg" />
          </ErpButtonRaw>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 mt-1">
          <DropdownMenuLabel className="font-semibold text-xs text-muted-foreground uppercase tracking-wider">
            {t('Aesthetic Theme')}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={() => setTheme('default')} 
            className={`cursor-pointer ${theme === 'default' ? 'bg-accent font-medium' : ''}`}
          >
            <Monitor size={16} className="mr-2 w-4 text-center opacity-70" /> Default
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => setTheme('neo-brutalist')} 
            className={`cursor-pointer ${theme === 'neo-brutalist' ? 'bg-accent font-medium' : ''}`}
          >
            <Package size={16} className="mr-2 w-4 text-center opacity-70" /> Neo-Brutalist
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => setTheme('luxury-dark')} 
            className={`cursor-pointer ${theme === 'luxury-dark' ? 'bg-accent font-medium' : ''}`}
          >
            <Gem size={16} className="mr-2 w-4 text-center opacity-70" /> Luxury Dark
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => setTheme('playful-pastel')} 
            className={`cursor-pointer ${theme === 'playful-pastel' ? 'bg-accent font-medium' : ''}`}
          >
            <Smile size={16} className="mr-2 w-4 text-center opacity-70" /> Playful Pastel
          </DropdownMenuItem>

          {customThemes && customThemes.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="font-semibold text-xs text-muted-foreground uppercase tracking-wider">
                {t('Custom Themes')}
              </DropdownMenuLabel>
              {customThemes
                // Custom themes come from localStorage JSON, so an entry can be
                // malformed. Without a usable id, setTheme/removeCustomTheme
                // would be called with undefined and duplicate keys collide.
                .filter((ct) => Boolean(ct) && typeof ct.id === 'string' && ct.id.length > 0)
                .map((ct) => (
                <ErpDiv key={ct.id} className={`flex items-center justify-between px-2 py-1.5 text-sm outline-none transition-colors rounded-sm ${theme === ct.id ? 'bg-accent font-medium' : 'hover:bg-accent hover:text-accent-foreground'}`}>
                  <ErpDiv 
                    className="flex items-center flex-1 cursor-pointer"
                    onClick={() => setTheme(ct.id)}
                  >
                    <ErpDiv 
                      className="w-4 h-4 rounded-full mr-2 border border-border" 
                      style={swatchStyle(ct.primaryColor)}
                    />
                    <ErpSpan className="truncate">{ct.name}</ErpSpan>
                  </ErpDiv>
                  <ErpButtonRaw 
                    onClick={(e) => { e.stopPropagation(); removeCustomTheme(ct.id); }}
                    className="text-muted-foreground hover:text-destructive opacity-50 hover:opacity-100 transition-opacity p-1 ml-2"
                    title={t('Delete Theme')}
                  >
                    <Trash2 size={14} />
                  </ErpButtonRaw>
                </ErpDiv>
              ))}
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={() => setShowBuilder(true)} 
            className="cursor-pointer text-indigo-600 focus:text-primary focus:bg-primary/10"
          >
            <Plus size={16} className="mr-2 w-4 text-center" /> {t('Create Custom Theme')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ThemeBuilderModal 
        isOpen={showBuilder} 
        onClose={() => setShowBuilder(false)} 
      />
    </>
  );
}
